import { useState, useMemo, useEffect } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import { PolicyForm2, type PolicyFormState } from './policy-form2';
import { PolicyComparisonContent } from './policy-change-comparison-dialog';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useAccounts } from '@/hooks/account/useAccounts';
import { useListGroups } from '@/hooks/organization/use-list-groups';
import { useListMembers } from '@/hooks/organization/use-list-members';
import type {
  Policy,
  PolicyTokenTransferCondition,
} from '@mls-wallet/shared-types';
import { buildPolicyTokenTransferCondition } from './build-policy-token-transfer-condition';
import { isPolicyFormSubmittable } from './policy-form-validation';

interface EditPolicyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policy: Policy | null;
  onPolicyUpdated: (policy: Policy) => void;
}

export function EditPolicyDialog({
  open,
  onOpenChange,
  policy,
  onPolicyUpdated,
}: EditPolicyDialogProps) {
  const [currentPage, setCurrentPage] = useState<'form' | 'comparison'>('form');
  const [direction, setDirection] = useState<'forward' | 'backward'>('forward');
  const [formData, setFormData] = useState<PolicyFormState | null>(null);
  const [modifiedPolicy, setModifiedPolicy] = useState<Policy | null>(null);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setCurrentPage('form');
      setDirection('forward');
      setFormData(null);
      setModifiedPolicy(null);
    }
  }, [open]);

  // Fetch organization data
  const { organization, isLoading: isOrgLoading } = useMyOrganization();

  // Fetch accounts, groups, and members
  const { accounts, isLoading: isAccountsLoading } = useAccounts();
  const { groups: rawGroups, isLoading: isGroupsLoading } = useListGroups(
    organization?.id ?? null
  );
  const { members: rawMembers, isLoading: isMembersLoading } = useListMembers(
    organization?.id ?? null
  );

  // Transform groups and members to the expected format
  // Memoized to prevent re-triggering the form's pre-fill effect
  const groups = useMemo(
    () =>
      rawGroups.map((group) => ({
        id: group.id,
        name: group.name,
        memberCount: group.members.length,
        type: 'group' as const,
      })),
    [rawGroups]
  );

  const members = useMemo(
    () =>
      rawMembers.map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        type: 'individual' as const,
      })),
    [rawMembers]
  );

  // Enrich policy with full account data (including networks array)
  // Memoized to prevent unnecessary re-renders of PolicyForm2
  // Recompute when policy.id changes or accounts list changes
  const enrichedPolicy = useMemo(() => {
    if (!policy) return null;

    return {
      ...policy,
      accounts: policy.accounts
        .map((policyAccount) => {
          const fullAccount = accounts.find((a) => a.id === policyAccount.id);
          return fullAccount || null;
        })
        .filter((a): a is NonNullable<typeof a> => a !== null),
    };
  }, [policy, accounts]);

  const handleFormDataChange = (data: PolicyFormState) => {
    setFormData(data);
  };

  const handleContinue = () => {
    if (!formData || !organization || !policy || !formData.networkId) return;

    // Build token transfer condition
    let tokenTransferCondition: PolicyTokenTransferCondition | null = null;
    if (formData.policyTransactionType === 'TOKEN_TRANSFER') {
      tokenTransferCondition = buildPolicyTokenTransferCondition(formData);
    }

    // Build contract interaction condition
    let contractInteractionCondition = null;
    if (formData.policyTransactionType === 'CONTRACT_INTERACTION') {
      const hasConditions =
        formData.contractAddressSelection.type === 'specific' ||
        formData.functionSelection.proxyFunctions.length > 0 ||
        formData.functionSelection.implementationFunctions.length > 0;

      if (hasConditions) {
        const contractAddressesArray: Array<{
          contractAddress: string;
          proxyFunctions: Array<{ name: string; selector: string }>;
          implementationFunctions: Array<{ name: string; selector: string }>;
        }> = [];

        if (formData.contractAddressSelection.type === 'specific') {
          contractAddressesArray.push({
            contractAddress: formData.contractAddressSelection.address,
            proxyFunctions: formData.functionSelection.proxyFunctions,
            implementationFunctions:
              formData.functionSelection.implementationFunctions,
          });
        }

        contractInteractionCondition = contractAddressesArray;
      }
    }

    // Map form accounts to policy accounts
    const policyAccounts = formData.selectedAccounts.map((account) => ({
      id: account.id,
      name: account.name,
      address: account.address,
    }));

    // Convert selectedInitiator to initiatorSetting format
    let initiatorSetting = null;
    if (formData.selectedInitiator) {
      if (formData.selectedInitiator.type === 'individual') {
        initiatorSetting = {
          type: 'user' as const,
          userId: formData.selectedInitiator.id,
          userName: formData.selectedInitiator.name,
        };
      } else if (formData.selectedInitiator.type === 'group') {
        // Look up the full group to get members
        const fullGroup = rawGroups.find(
          (g) => g.id === formData.selectedInitiator?.id
        );
        initiatorSetting = {
          type: 'group' as const,
          groupId: formData.selectedInitiator.id,
          groupName: formData.selectedInitiator.name,
          members:
            fullGroup?.members.map((m) => ({ id: m.id, name: m.name })) ?? [],
        };
      }
    }

    // Convert selectedApprover to approverSetting format
    let approverSetting = null;
    if (
      formData.policyType === 'MANUAL_APPROVAL' &&
      formData.selectedApprover
    ) {
      if (formData.selectedApprover.type === 'individual') {
        approverSetting = {
          type: 'user' as const,
          userId: formData.selectedApprover.id,
          userName: formData.selectedApprover.name,
        };
      } else if (formData.selectedApprover.type === 'group') {
        // Look up the full group to get members
        const fullGroup = rawGroups.find(
          (g) => g.id === formData.selectedApprover?.id
        );
        approverSetting = {
          type: 'group' as const,
          groupId: formData.selectedApprover.id,
          groupName: formData.selectedApprover.name,
          threshold: formData.approverThreshold ?? 1,
          members:
            fullGroup?.members.map((m) => ({ id: m.id, name: m.name })) ?? [],
        };
      }
    }

    // Build the updated Policy object, preserving existing fields
    let updatedPolicy: Policy;
    const basePolicy = {
      id: policy.id,
      onchainId: policy.onchainId, // Preserve existing onchainId
      name: formData.policyName,
      description: formData.policyDescription || null,
      type: formData.policyType,
      networkId: formData.networkId,
      organizationId: policy.organizationId,
      accounts: policyAccounts,
      limitation: formData.limitation,
      initiatorSetting,
      approverSetting,
      isDeleted: policy.isDeleted,
      createdAt: policy.createdAt,
      updatedAt: new Date(), // Will be updated by backend, but set for local state
    };

    if (formData.policyTransactionType === 'TOKEN_TRANSFER') {
      updatedPolicy = {
        ...basePolicy,
        transactionType: 'TOKEN_TRANSFER',
        tokenTransferCondition,
      };
    } else if (formData.policyTransactionType === 'CONTRACT_INTERACTION') {
      updatedPolicy = {
        ...basePolicy,
        transactionType: 'CONTRACT_INTERACTION',
        contractInteractionCondition,
      };
    } else {
      updatedPolicy = {
        ...basePolicy,
        transactionType: 'ANY',
      };
    }

    // Store the modified policy and navigate to comparison page
    setModifiedPolicy(updatedPolicy);
    setDirection('forward');
    setCurrentPage('comparison');
  };

  const handleSubmitChanges = () => {
    if (!modifiedPolicy) return;
    // Pass the updated policy back to parent
    onPolicyUpdated(modifiedPolicy);
    onOpenChange(false);
  };

  const handleBackToEdit = () => {
    setDirection('backward');
    setCurrentPage('form');
  };

  const isLoading =
    isOrgLoading || isAccountsLoading || isGroupsLoading || isMembersLoading;

  const canSubmit =
    isPolicyFormSubmittable(formData) && !isLoading && Boolean(policy);

  // Dynamic title based on current page
  const getDialogTitle = () => {
    return currentPage === 'form'
      ? 'Edit policy'
      : 'Review policy changes before submitting';
  };

  // Dynamic footer based on current page
  const getDialogFooter = () => {
    if (currentPage === 'form') {
      return (
        <div className="flex justify-center gap-2 max-w-xl w-full px-6">
          <Button
            size="lg"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button size="lg" onClick={handleContinue} disabled={!canSubmit}>
            Continue
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      );
    } else {
      return (
        <div className="flex justify-center gap-2 max-w-xl w-full px-6">
          <Button size="lg" variant="outline" onClick={handleBackToEdit}>
            Back
          </Button>
          <Button size="lg" onClick={handleSubmitChanges}>
            <Check className="h-4 w-4 mr-1" />
            Submit changes
          </Button>
        </div>
      );
    }
  };

  // Render comparison content
  const renderComparisonContent = () => {
    if (!policy || !modifiedPolicy) return null;
    return (
      <PolicyComparisonContent
        originalPolicy={policy}
        modifiedPolicy={modifiedPolicy}
      />
    );
  };

  // Dynamic content with animation based on current page
  const getDialogContent = () => {
    const slideAnimationDuration = 0.2;
    return (
      <div className="relative w-full h-full">
        <motion.div
          initial={false}
          animate={
            currentPage === 'form'
              ? { x: 0, opacity: 1 }
              : {
                  x: direction === 'forward' ? '-100%' : '100%',
                  opacity: 0,
                }
          }
          transition={{
            x: { duration: slideAnimationDuration, ease: 'easeInOut' },
            opacity: {
              duration: slideAnimationDuration,
              ease: 'easeInOut',
            },
          }}
          className="absolute inset-0 h-full w-full overflow-y-auto"
          style={{
            pointerEvents: currentPage === 'form' ? 'auto' : 'none',
            visibility: currentPage === 'form' ? 'visible' : 'hidden',
          }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : (
            <PolicyForm2
              initialData={enrichedPolicy ?? undefined}
              onDataChange={handleFormDataChange}
              sectionVariant="muted"
              accounts={accounts}
              groups={groups}
              members={members}
            />
          )}
        </motion.div>

        <motion.div
          initial={false}
          animate={
            currentPage === 'comparison'
              ? { x: 0, opacity: 1 }
              : {
                  x: direction === 'forward' ? '100%' : '-100%',
                  opacity: 0,
                }
          }
          transition={{
            x: { duration: slideAnimationDuration, ease: 'easeInOut' },
            opacity: {
              duration: slideAnimationDuration,
              ease: 'easeInOut',
            },
          }}
          className="absolute inset-0 h-full w-full overflow-y-auto bg-background"
          style={{
            pointerEvents: currentPage === 'comparison' ? 'auto' : 'none',
            visibility: currentPage === 'comparison' ? 'visible' : 'hidden',
          }}
        >
          {renderComparisonContent()}
        </motion.div>
      </div>
    );
  };

  return (
    <NestedFullscreenDialog
      open={open}
      onOpenChange={onOpenChange}
      title={getDialogTitle()}
      footer={getDialogFooter()}
      noPadding={true}
      bodyContainerClassName="bg-muted"
    >
      {getDialogContent()}
    </NestedFullscreenDialog>
  );
}
